import { z } from 'zod';

/**
 * Funds, funding and money-movement contracts.
 *
 * ONE balance. Deposits and prizes raise it, orders take margin out of it and
 * hand it back on exit, and withdrawals are paid out of it — there is nothing
 * to transfer it to and no second figure to reconcile against. Two columns
 * carry it server-side, but both count toward buying power and a withdrawal
 * sweeps between them on its own, so the split never surfaces.
 *
 * Every movement in or out of the platform writes one `wallet_ledger` row, so
 * the statement accounts for every rupee that arrived or left.
 */

/** Ledger row types. The sign of `amount` is always from the WALLET's point of view. */
export const LedgerKindSchema = z.enum([
  /** Money in from the payment gateway. */
  'deposit',
  /** Debited at withdrawal-request time (held until ops pays or rejects). */
  'withdrawal',
  /** A rejected withdrawal returning to the balance. */
  'withdrawal_refund',
  /**
   * Internal sweep between the two server-side columns. Both directions are
   * bookkeeping against the one balance the trader is shown, so they net to
   * zero and the statement hides them; the rows are written all the same, and
   * the operator console lists every one.
   */
  'transfer_to_trading',
  'transfer_from_trading',
  /** Challenge payout credited by the engine. */
  'prize',
  /** Manual operator correction. */
  'adjustment',
]);
export type LedgerKind = z.infer<typeof LedgerKindSchema>;

export const LedgerEntrySchema = z.object({
  id: z.string(),
  kind: LedgerKindSchema,
  /** Signed rupees: positive credits the balance, negative debits it. */
  amount: z.number(),
  /** Human label for the statement line. */
  label: z.string(),
  /** Originating row id (deposit / withdrawal / enrollment), when there is one. */
  ref: z.string().nullable(),
  at: z.number(),
});
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>;

/** The funds screen, entire. */
export const WalletSummarySchema = z.object({
  /**
   * THE balance. Spendable money, wherever it happens to sit server-side:
   * this is both what a new order may consume and what may be withdrawn, so a
   * trader is never told they have money they cannot use for the thing in
   * front of them. Margin posted against an open position is already out of
   * it, and comes back when the position closes.
   */
  funds: z.number(),
  /** Whether this account may withdraw (guests may not). */
  canWithdraw: z.boolean(),
});
export type WalletSummary = z.infer<typeof WalletSummarySchema>;

/**
 * Deposit intent. For manual bank transfer, the user provides the transaction
 * reference / UTR along with the amount.
 */
export const DepositRequestSchema = z.object({
  /** Whole rupees. */
  amount: z.number().int().min(100, 'Minimum deposit is ₹100').max(10_000_000),
  /** Transaction reference / UTR entered by user */
  utr: z.string().trim().min(4, 'Enter a valid UTR / transaction reference number').max(64),
});
export type DepositRequest = z.infer<typeof DepositRequestSchema>;

export const DepositStatusSchema = z.enum(['created', 'pending', 'paid', 'rejected', 'failed']);
export type DepositStatus = z.infer<typeof DepositStatusSchema>;

/**
 * Deposit response returned upon submitting manual deposit or legacy gateway.
 */
export const DepositResponseSchema = z.object({
  depositId: z.string(),
  amount: z.number(),
  utr: z.string().optional(),
  status: DepositStatusSchema,
  estimatedHours: z.number().optional(),
  message: z.string().optional(),
  // Optional fields for legacy Razorpay checkout compatibility if ever used
  razorpayOrderId: z.string().optional(),
  currency: z.literal('INR').optional(),
  key: z.string().optional(),
});
export type DepositResponse = z.infer<typeof DepositResponseSchema>;

export const DepositVerifyRequestSchema = z.object({
  depositId: z.string(),
  razorpayOrderId: z.string(),
  razorpayPaymentId: z.string(),
  razorpaySignature: z.string(),
});
export type DepositVerifyRequest = z.infer<typeof DepositVerifyRequestSchema>;

export const DepositSchema = z.object({
  id: z.string(),
  amount: z.number(),
  status: DepositStatusSchema,
  utr: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  createdAt: z.number(),
  paidAt: z.number().nullable(),
  estimatedHours: z.number().optional(),
});
export type Deposit = z.infer<typeof DepositSchema>;

export const DepositBankDetailsSchema = z.object({
  bankName: z.string(),
  accountHolder: z.string(),
  accountNumber: z.string(),
  ifsc: z.string(),
  upiId: z.string().optional(),
});
export type DepositBankDetails = z.infer<typeof DepositBankDetailsSchema>;
